/**
 * KollectCare AI - Main Layout Component
 * Professional sidebar navigation with responsive design
 */

import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useStore, useUIStore, useNotificationStore } from '../store/authStore';
import { 
  LayoutDashboard, 
  Users, 
  Send, 
  Zap, 
  FileText, 
  BarChart3, 
  Settings, 
  LogOut, 
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  HelpCircle,
  Moon,
  Sun,
  CheckCircle,
  AlertCircle,
  Info,
  XCircle,
  Inbox,
  Filter,
  Radio,
  PieChart,
  Link2,
  Plus,
  Building2,
  Sparkles,
  Clock,
  TrendingUp,
  Wifi,
  WifiOff,
  Command,
  Target
} from 'lucide-react';

// Toast Notification Component
function ToastContainer() {
  const { notifications, removeNotification } = useNotificationStore();

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle size={20} />;
      case 'error': return <XCircle size={20} />;
      case 'warning': return <AlertCircle size={20} />;
      default: return <Info size={20} />;
    }
  };

  useEffect(() => {
    // Auto-remove notifications after 5 seconds
    const timers = notifications.map((notification) =>
      setTimeout(() => removeNotification(notification.id), 5000)
    );
    return () => timers.forEach(clearTimeout);
  }, [notifications, removeNotification]);

  return (
    <div className="toast-container">
      {notifications.map((notification) => (
        <div 
          key={notification.id} 
          className={`toast toast-${notification.type || 'info'}`}
        >
          <span className="toast-icon">{getIcon(notification.type)}</span>
          <div className="toast-content">
            {notification.title && <strong>{notification.title}</strong>}
            <p>{notification.message}</p>
          </div>
          <button 
            className="toast-close" 
            onClick={() => removeNotification(notification.id)}
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

// Breadcrumb Component
function Breadcrumb() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const breadcrumbNames = {
    'dashboard': 'Dashboard',
    'inbox': 'Inbox',
    'contacts': 'Contacts',
    'segments': 'Segments',
    'campaigns': 'Campaigns',
    'broadcasts': 'Broadcasts',
    'workflows': 'Automation',
    'templates': 'Templates',
    'analytics': 'Analytics',
    'reports': 'Reports',
    'integrations': 'Integrations',
    'settings': 'Settings',
    'help': 'Help Center',
    'new': 'New',
  };

  if (pathnames.length === 0) return null;

  return (
    <nav className="breadcrumb">
      <Link to="/" className="breadcrumb-item">
        <LayoutDashboard size={14} />
        <span>Home</span>
      </Link>
      {pathnames.map((name, index) => {
        const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
        const isLast = index === pathnames.length - 1;
        const displayName = breadcrumbNames[name] || name;

        return (
          <React.Fragment key={name}>
            <ChevronRight size={14} className="breadcrumb-separator" />
            {isLast ? (
              <span className="breadcrumb-item current">{displayName}</span>
            ) : (
              <Link to={routeTo} className="breadcrumb-item">
                {displayName}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// Workspace Selector Component
function WorkspaceSelector({ collapsed }) {
  const [isOpen, setIsOpen] = useState(false);
  const workspaces = [
    { id: 1, name: 'KollectCare Main', plan: 'Pro', active: true },
    { id: 2, name: 'Marketing Team', plan: 'Business', active: false },
  ];
  const activeWorkspace = workspaces.find(w => w.active);

  if (collapsed) {
    return (
      <div className="workspace-selector collapsed" title={activeWorkspace?.name}>
        <div className="workspace-avatar">
          {activeWorkspace?.name.charAt(0)}
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-selector" onClick={() => setIsOpen(!isOpen)}>
      <div className="workspace-avatar">
        {activeWorkspace?.name.charAt(0)}
      </div>
      <div className="workspace-info">
        <span className="workspace-name">{activeWorkspace?.name}</span>
        <span className="workspace-plan">{activeWorkspace?.plan} Plan</span>
      </div>
      <ChevronDown size={16} className={`workspace-chevron ${isOpen ? 'open' : ''}`} />
      
      {isOpen && (
        <div className="workspace-dropdown" onClick={(e) => e.stopPropagation()}>
          {workspaces.map((workspace) => (
            <div 
              key={workspace.id} 
              className={`workspace-option ${workspace.active ? 'active' : ''}`}
            >
              <div className="workspace-avatar small">
                {workspace.name.charAt(0)}
              </div>
              <div className="workspace-option-info">
                <span>{workspace.name}</span>
                <span className="workspace-plan">{workspace.plan}</span>
              </div>
              {workspace.active && <CheckCircle size={16} className="check-icon" />}
            </div>
          ))}
          <div className="workspace-dropdown-divider" />
          <button className="workspace-option add-new">
            <Plus size={16} />
            <span>Add Workspace</span>
          </button>
        </div>
      )}
    </div>
  );
}

// Search Modal Component
function SearchModal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const recentSearches = ['Active campaigns', 'John Doe contact', 'Welcome template'];
  
  const searchResults = [
    { type: 'page', icon: Users, label: 'Contacts', path: '/contacts' },
    { type: 'page', icon: Send, label: 'Campaigns', path: '/campaigns' },
    { type: 'page', icon: FileText, label: 'Templates', path: '/templates' },
    { type: 'page', icon: Inbox, label: 'Inbox', path: '/inbox' },
    { type: 'page', icon: BarChart3, label: 'Analytics', path: '/analytics' },
  ];

  const quickActions = [
    { icon: Plus, label: 'New Campaign', path: '/campaigns/new' },
    { icon: Users, label: 'Import Contacts', path: '/contacts?action=import' },
    { icon: FileText, label: 'Create Template', path: '/templates/new' },
    { icon: Send, label: 'Send Broadcast', path: '/broadcasts/new' },
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay search-overlay" onClick={onClose}>
      <div className="search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="search-modal-input">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search pages, contacts, campaigns..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <kbd>ESC</kbd>
        </div>
        
        {!query && (
          <>
            <div className="search-section">
              <h4><Sparkles size={14} /> Quick Actions</h4>
              <div className="quick-actions-grid">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    className="quick-action-btn"
                    onClick={() => {
                      navigate(action.path);
                      onClose();
                    }}
                  >
                    <action.icon size={18} />
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="search-section">
              <h4><Clock size={14} /> Recent Searches</h4>
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  className="search-result-item"
                  onClick={() => setQuery(search)}
                >
                  <Clock size={16} />
                  <span>{search}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="search-section">
          <h4><Target size={14} /> Pages</h4>
          <div className="search-modal-results">
            {searchResults
              .filter(item => item.label.toLowerCase().includes(query.toLowerCase()))
              .map((item, index) => (
                <button
                  key={index}
                  className="search-result-item"
                  onClick={() => {
                    navigate(item.path);
                    onClose();
                  }}
                >
                  <item.icon size={16} />
                  <span>{item.label}</span>
                  <ChevronRight size={14} className="result-arrow" />
                </button>
              ))}
          </div>
        </div>

        <div className="search-modal-footer">
          <span><kbd>↑↓</kbd> Navigate</span>
          <span><kbd>↵</kbd> Select</span>
          <span><kbd>ESC</kbd> Close</span>
        </div>
      </div>
    </div>
  );
}

// Status Bar Component
function StatusBar() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className="status-bar">
      <div className="status-bar-left">
        <span className={`connection-status ${isOnline ? 'online' : 'offline'}`}>
          {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
          {isOnline ? 'Connected' : 'Offline'}
        </span>
        <span className="status-divider">•</span>
        <span className="api-status">
          <span className="status-dot success"></span>
          WhatsApp API: Active
        </span>
      </div>
      <div className="status-bar-right">
        <span>KollectCare AI v2.0</span>
        <span className="status-divider">•</span>
        <a href="/help" className="status-link">Documentation</a>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useStore();
  const { sidebarCollapsed, toggleSidebar, theme, setTheme } = useUIStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClick = () => {
      setUserMenuOpen(false);
      setNotificationsOpen(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Grouped Navigation Items
  const navSections = [
    {
      id: 'core',
      title: 'Core',
      items: [
        { path: '/', label: 'Dashboard', icon: LayoutDashboard, badge: null },
        { path: '/inbox', label: 'Inbox', icon: Inbox, badge: '5' },
        { path: '/contacts', label: 'Contacts', icon: Users, badge: null },
        { path: '/segments', label: 'Segments', icon: Filter, badge: null },
      ]
    },
    {
      id: 'marketing',
      title: 'Marketing',
      items: [
        { path: '/campaigns', label: 'Campaigns', icon: Send, badge: '3' },
        { path: '/broadcasts', label: 'Broadcasts', icon: Radio, badge: null },
        { path: '/workflows', label: 'Automation', icon: Zap, badge: null },
        { path: '/templates', label: 'Templates', icon: FileText, badge: null },
      ]
    },
    {
      id: 'insights',
      title: 'Insights',
      items: [
        { path: '/analytics', label: 'Analytics', icon: BarChart3, badge: null },
        { path: '/reports', label: 'Reports', icon: PieChart, badge: null },
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      items: [
        { path: '/integrations', label: 'Integrations', icon: Link2, badge: null },
        { path: '/settings', label: 'Settings', icon: Settings, badge: null },
      ]
    },
  ];

  const mockNotifications = [
    { id: 1, type: 'success', message: 'Campaign "Summer Sale" sent successfully', time: '5m ago', read: false },
    { id: 2, type: 'info', message: '15 new contacts imported', time: '1h ago', read: false },
    { id: 3, type: 'warning', message: 'Workflow "Welcome Series" needs attention', time: '2h ago', read: true },
    { id: 4, type: 'success', message: 'Template "Promo" approved', time: '3h ago', read: true },
  ];

  const unreadCount = mockNotifications.filter(n => !n.read).length;

  return (
    <div className={`app-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${theme}`}>
      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Workspace Selector */}
        <div className="sidebar-header">
          <WorkspaceSelector collapsed={sidebarCollapsed} />
          <button 
            className="sidebar-toggle desktop-only"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Menu size={20} />
          </button>
          <button 
            className="sidebar-close mobile-only"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X size={24} />
          </button>
        </div>

        {/* Quick Search in Sidebar */}
        {!sidebarCollapsed && (
          <div className="sidebar-search" onClick={() => setSearchOpen(true)}>
            <Search size={16} />
            <span>Quick search...</span>
            <kbd><Command size={10} />K</kbd>
          </div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navSections.map((section) => (
            <div key={section.id} className="nav-section">
              {!sidebarCollapsed && (
                <button 
                  className="nav-section-header"
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="nav-section-title">{section.title}</span>
                  <ChevronDown 
                    size={14} 
                    className={`section-chevron ${collapsedSections[section.id] ? 'collapsed' : ''}`} 
                  />
                </button>
              )}
              
              {!collapsedSections[section.id] && section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== '/' && location.pathname.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <Icon size={20} />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                    {item.badge && !sidebarCollapsed && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                    {item.badge && sidebarCollapsed && (
                      <span className="nav-badge-dot"></span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}

          {!sidebarCollapsed && (
            <div className="nav-section">
              <span className="nav-section-title">Support</span>
              <Link to="/help" className="nav-item">
                <HelpCircle size={20} />
                <span>Help Center</span>
              </Link>
            </div>
          )}
        </nav>

        {/* Upgrade Banner */}
        {!sidebarCollapsed && (
          <div className="upgrade-banner">
            <div className="upgrade-icon">
              <TrendingUp size={20} />
            </div>
            <div className="upgrade-content">
              <strong>Upgrade to Pro</strong>
              <p>Get unlimited messages & advanced features</p>
            </div>
            <button className="btn btn-sm btn-primary">Upgrade</button>
          </div>
        )}

        {/* User Section */}
        <div className="sidebar-footer">
          {!sidebarCollapsed ? (
            <div className="user-card">
              <div className="user-avatar">
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </div>
              <div className="user-info">
                <span className="user-name">{user?.firstName} {user?.lastName}</span>
                <span className="user-role">{user?.role || 'Admin'}</span>
              </div>
              <button 
                className="btn-icon" 
                onClick={handleLogout} 
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <button 
              className="nav-item" 
              onClick={handleLogout}
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          )}
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <div className="main-wrapper">
        {/* Top Header */}
        <header className="top-header">
          <div className="header-left">
            <button 
              className="btn-icon mobile-only"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
            
            <Breadcrumb />
          </div>

          <div className="header-center">
            <button 
              className="search-trigger"
              onClick={() => setSearchOpen(true)}
            >
              <Search size={18} />
              <span className="desktop-only">Search...</span>
              <kbd className="desktop-only"><Command size={10} />K</kbd>
            </button>
          </div>

          <div className="header-right">
            {/* Theme Toggle */}
            <button 
              className="btn-icon"
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            </button>

            {/* Notifications */}
            <div className="dropdown-wrapper" onClick={(e) => e.stopPropagation()}>
              <button 
                className="btn-icon notification-btn"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </button>
              
              {notificationsOpen && (
                <div className="dropdown-menu notifications-dropdown">
                  <div className="dropdown-header">
                    <h4>Notifications</h4>
                    <button className="btn-link">Mark all read</button>
                  </div>
                  <div className="dropdown-content">
                    {mockNotifications.map((notif) => (
                      <div key={notif.id} className={`notification-item ${notif.type} ${!notif.read ? 'unread' : ''}`}>
                        <div className="notification-icon">
                          {notif.type === 'success' && <CheckCircle size={16} />}
                          {notif.type === 'info' && <Info size={16} />}
                          {notif.type === 'warning' && <AlertCircle size={16} />}
                        </div>
                        <div className="notification-content">
                          <p>{notif.message}</p>
                          <span>{notif.time}</span>
                        </div>
                        {!notif.read && <span className="unread-indicator"></span>}
                      </div>
                    ))}
                  </div>
                  <div className="dropdown-footer">
                    <button className="btn-link">View all notifications</button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="dropdown-wrapper" onClick={(e) => e.stopPropagation()}>
              <button 
                className="user-menu-trigger"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="user-avatar-small">
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </div>
                <span className="desktop-only user-name-header">{user?.firstName}</span>
                <ChevronDown size={16} />
              </button>
              
              {userMenuOpen && (
                <div className="dropdown-menu user-dropdown">
                  <div className="dropdown-user-info">
                    <div className="user-avatar">
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </div>
                    <div>
                      <strong>{user?.firstName} {user?.lastName}</strong>
                      <span>{user?.email}</span>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  <Link to="/settings" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                    <Settings size={16} />
                    <span>Settings</span>
                  </Link>
                  <Link to="/settings?tab=billing" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                    <BarChart3 size={16} />
                    <span>Billing</span>
                  </Link>
                  <Link to="/help" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                    <HelpCircle size={16} />
                    <span>Help & Support</span>
                  </Link>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item text-danger" onClick={handleLogout}>
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="main-content">
          <Outlet />
        </main>

        {/* Status Bar */}
        <StatusBar />
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  );
}
